/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  type FollowUp,
  type FollowUpPriority,
  type FollowUpStatus,
} from '../../lib/api/follow-ups.js';
import { useFollowUps } from '../../lib/queries/follow-ups.js';
import { formatIst } from '../../lib/utils/datetime.js';

import { CreateFollowUpModal } from './follow-up-modal.js';
import { FollowUpSlaPanel } from './followup-sla-panel.js';
import { ResolveFollowUpModal } from './resolve-followup-modal.js';

const PRIORITY_TONE: Readonly<Record<FollowUpPriority, BadgeTone>> = {
  urgent: 'rose',
  high: 'amber',
  medium: 'violet',
  low: 'slate',
};
const STATUS_TONE: Readonly<Record<FollowUpStatus, BadgeTone>> = {
  open: 'violet',
  in_progress: 'amber',
  resolved: 'green',
};
const TABS: readonly ('all' | FollowUpStatus)[] = ['all', 'open', 'in_progress', 'resolved'];
const COLS = ['type', 'patient', 'priority', 'sla', 'created', 'status', 'assigned'] as const;

function FollowUpRow({
  item,
  onResolve,
}: {
  readonly item: FollowUp;
  readonly onResolve: (item: FollowUp) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const resolve = useCallback(() => {
    onResolve(item);
  }, [onResolve, item]);
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">
        {t(`followUps.types.${item.type}`)}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{item.clientName ?? '—'}</td>
      <td className="px-5 py-3">
        <Badge
          label={t(`followUps.priorities.${item.priority}`)}
          tone={PRIORITY_TONE[item.priority]}
        />
      </td>
      <td className="px-5 py-3">
        {item.slaDueAt !== null ? (
          <span
            className={
              item.overdue ? 'font-medium text-rose-600' : 'text-slate-600 dark:text-slate-300'
            }
          >
            {formatIst(item.slaDueAt, 'dd MMM yyyy')}
            {item.overdue ? ` · ${t('followUps.overdue')}` : ''}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {formatIst(item.createdAt, 'dd MMM yyyy')}
      </td>
      <td className="px-5 py-3">
        <Badge label={t(`followUps.statuses.${item.status}`)} tone={STATUS_TONE[item.status]} />
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{item.assignedToName ?? '—'}</td>
      <td className="px-5 py-3 text-right">
        {item.status !== 'resolved' && (
          <Button variant="ghost" size="sm" onClick={resolve}>
            {t('followUps.resolve')}
          </Button>
        )}
      </td>
    </tr>
  );
}

/** Follow-up escalation dashboard (S11). */
export function FollowUpsPage(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'all' | FollowUpStatus>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [resolving, setResolving] = useState<FollowUp | null>(null);
  const query = useFollowUps(tab === 'all' ? {} : { status: tab });
  const rows = query.data ?? [];
  const onTab = useCallback((value: 'all' | FollowUpStatus) => {
    setTab(value);
  }, []);
  const openCreate = useCallback(() => {
    setCreateOpen(true);
  }, []);
  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);
  const closeResolve = useCallback(() => {
    setResolving(null);
  }, []);

  return (
    <div className="p-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <PageHeader title={t('followUps.title')} subtitle={t('followUps.subtitle')} />
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('followUps.newTitle')}
        </Button>
      </div>

      <FollowUpSlaPanel />

      <div className="mb-4 flex flex-wrap gap-2" role="tablist">
        {TABS.map((value) => (
          <TabButton key={value} value={value} active={tab === value} onSelect={onTab} />
        ))}
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('followUps.empty')}
      >
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                {COLS.map((c) => (
                  <th key={c} className="px-5 py-3 font-semibold">
                    {t(`followUps.col.${c}`)}
                  </th>
                ))}
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((item) => (
                <FollowUpRow key={item.id} item={item} onResolve={setResolving} />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>

      <CreateFollowUpModal open={createOpen} onClose={closeCreate} />
      <ResolveFollowUpModal followUp={resolving} onClose={closeResolve} />
    </div>
  );
}

function TabButton({
  value,
  active,
  onSelect,
}: {
  readonly value: 'all' | FollowUpStatus;
  readonly active: boolean;
  readonly onSelect: (value: 'all' | FollowUpStatus) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const onClick = useCallback(() => {
    onSelect(value);
  }, [onSelect, value]);
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`focus-ring rounded-full px-4 py-1.5 text-sm font-medium ${active ? 'bg-[var(--accent)] text-white' : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'}`}
    >
      {value === 'all' ? t('followUps.tabAll') : t(`followUps.statuses.${value}`)}
    </button>
  );
}
